import { useInfiniteQuery } from '~/shared/hooks/query'
import {
  providersWorkUnitsPeopleTodos,
  ProvidersWorkUnitsPeopleTodosListResponse,
} from '@fireflyhealth/api'
import { useCallback, useState } from 'react'
import flatten from 'lodash/flatten'
import { listPersonTodoKey } from '~/shared/lib/queryKeys'

// Type aliases for auto-generated types - these are the canonical types for todo data
export type RawTodoResult = ProvidersWorkUnitsPeopleTodosListResponse['results'][number]
export type RawTodoItem = RawTodoResult['items'][number]

// Content-type specific narrowing using Extract with content_type discriminator
type RawTodoCaseContentObject = Extract<RawTodoResult['content_object'], { content_type: 'case' }>

export type RawTodoCaseResult = RawTodoResult & {
  content_type: 'case'
  content_object: RawTodoCaseContentObject
}
export type RawTodoRelation = NonNullable<RawTodoCaseContentObject['relations']>[number]

type RawTodoCarePlanContentObject = Extract<
  RawTodoResult['content_object'],
  { content_type: 'careplan' }
>
export type RawTodoCarePlanResult = RawTodoResult & {
  content_type: 'careplan'
  content_object: RawTodoCarePlanContentObject
}

type RawTodoChatMessageContentObject = Extract<
  RawTodoResult['content_object'],
  { content_type: 'chatmessagev2' }
>
export type RawTodoChatMessageResult = RawTodoResult & {
  content_type: 'chatmessagev2'
  content_object: RawTodoChatMessageContentObject
}

// Type predicates for safer type narrowing
export function isCaseTodo(todo: RawTodoResult): todo is RawTodoCaseResult {
  return todo.content_type === 'case'
}

export function isCarePlanTodo(todo: RawTodoResult): todo is RawTodoCarePlanResult {
  return todo.content_type === 'careplan'
}

type PatientTodoFilters =
  | {
      isComplete?: boolean
    }
  | {
      contentType: string
      objectId: number
    }

export type PatientTodoTabs = 'active' | 'complete'

type PageParam = number | { offset: number; isComplete: boolean }

function mapParams(
  filters: PatientTodoFilters,
  limit: number,
  pageParam: PageParam,
): Record<string, unknown> {
  // Subsequent pages after a goTo: the backend's next/previous URLs contain is_complete + offset,
  // so we use those directly instead of re-sending content_type/object_id (which would repeat the
  // same initial request and cause duplicates).
  if (typeof pageParam === 'object') {
    return {
      is_complete: pageParam.isComplete,
      limit,
      offset: pageParam.offset,
    }
  }
  if ('contentType' in filters) {
    return {
      content_type: filters.contentType,
      object_id: filters.objectId,
      limit,
    }
  }
  return {
    is_complete: !!filters.isComplete,
    limit,
    offset: pageParam,
  }
}

function extractPageParam(url: string | null | undefined): PageParam | undefined {
  if (!url) return undefined
  try {
    const parsedUrl = new URL(url)
    const offset = parsedUrl.searchParams.get('offset')
    const isComplete = parsedUrl.searchParams.get('is_complete')
    const parsedOffset = offset !== null ? Number(offset) : NaN
    if (Number.isNaN(parsedOffset)) return undefined
    // If is_complete is present, return a structured page param so mapParams can use it directly
    if (isComplete !== null) {
      return { offset: parsedOffset, isComplete: isComplete.toLowerCase() === 'true' }
    }
    return parsedOffset
  } catch {
    return undefined
  }
}

export const usePersonTodos = (personId: number) => {
  const limit = 50

  const [tab, setTab] = useState<PatientTodoTabs>('active')
  const [filters, setFilters] = useState<PatientTodoFilters>({
    isComplete: tab === 'complete',
  })

  const {
    data,
    hasNextPage,
    fetchNextPage,
    hasPreviousPage,
    fetchPreviousPage,
    isPending: isLoading,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useInfiniteQuery(
    [listPersonTodoKey, personId, filters],
    async ({
      pageParam,
    }: {
      pageParam: PageParam
    }): Promise<ProvidersWorkUnitsPeopleTodosListResponse> => {
      const apiParams = mapParams(filters, limit, pageParam)
      const response = await providersWorkUnitsPeopleTodos.list(
        { provider_id: 'me', person_id: personId },
        { params: apiParams },
      )
      return response.data
    },
    {
      initialPageParam: 0,
      getNextPageParam: lastPage => extractPageParam(lastPage.next),
      getPreviousPageParam: firstPage => extractPageParam(firstPage.previous),
    },
    {
      error: 'Failed to fetch to dos for patient',
    },
  )

  const resetFilters = useCallback(() => {
    setFilters({
      isComplete: tab === 'complete',
    })
  }, [setFilters, tab])

  // Used when the caller sets the tab explicitly, which should also update the filters and trigger a new query.
  const handleSetTab = useCallback(
    (tab: Parameters<typeof setTab>[0]) => {
      setTab(tab)
      setFilters({
        isComplete: tab === 'complete',
      })
    },
    [setTab, setFilters],
  )

  // Go to the tab and page containing the given object.
  const goTo = useCallback(
    (identifier: { contentType: string; objectId: number }) => {
      if (!identifier.contentType || !identifier.objectId) {
        return
      }

      setFilters({ contentType: identifier.contentType, objectId: identifier.objectId })
    },
    [setFilters],
  )

  // When we go to a specific object, there is a period of time where we don't know the tab we should be on. Once we
  // get the data back, set the correct tab.
  const [tabResolvedFrom, setTabResolvedFrom] = useState<{
    filters: PatientTodoFilters
    data: typeof data
  }>()
  const isNewObjectResult = tabResolvedFrom?.filters !== filters || tabResolvedFrom?.data !== data
  if ('contentType' in filters && isNewObjectResult) {
    setTabResolvedFrom({ filters, data })
    setTab(data?.pages[0]?.params?.is_complete ? 'complete' : 'active')
  }

  return {
    data: flatten(data?.pages.map(page => page.results)),
    tab,
    setTab: handleSetTab,
    hasNextPage,
    fetchNextPage,
    hasPreviousPage,
    fetchPreviousPage,
    goTo,
    isLoading,
    isFetchingNextPage,
    isFetchingPreviousPage,
    resetFilters,
  }
}
